import { Shield, Settings, User, LogOut, ChevronDown } from 'lucide-react';

import { Link, Button, Badge } from '@/components/design-system';
import {
  COLOR_SCALE,
  FONT_WEIGHTS,
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  RADIUS_SCALE,
  SIZE_SCALE,
  SURFACE_PADDING,
  TYPE_SCALE,
  INTERACTIVE_STATES,
  SHADOW_SCALE,
  BORDER_WIDTH,
  MARGIN_LEFT,
  SPACE_Y_SCALE,
  TEXT_ALIGNMENT,
  FLEX,
  FLEX_ALIGN,
} from '@/components/design-system/tokens';
import type { Json } from '@/integrations/supabase/types';
import { createPageUrl } from '@/utils';

// Extended SIZE_SCALE type to include missing semantic tokens
type ExtendedSizeScale = typeof SIZE_SCALE & {
  iconMd: string;
  minH24: string;
};

interface UserSectionUser {
  role?: string;
  is_admin?: boolean;
  is_marketer?: boolean;
  is_partner?: boolean | null;
  partner_category?: string | null;
  assigned_provider_ids?: string | string[] | null;
  partner_permissions?: Json | null;
  profile_image_url?: string | null;
  full_name?: string | null;
  email?: string | null;
}

interface UserSectionProps {
  user: UserSectionUser | null;
  newInquiryCount: number;
  adminOpen: boolean;
  partnerOpen: boolean;
  onAdminToggle: () => void;
  onPartnerToggle: () => void;
  handleLogin: () => void;
  handleSignup: () => void;
  handleLogout: () => void;
  onClose: () => void;
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string;
}

// Helper function to render admin section
const renderAdminSection = (
  user: UserSectionUser,
  adminOpen: boolean,
  onAdminToggle: () => void,
  onClose: () => void,
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string
) => {
  if (!user.is_admin) return null;

  return (
    <div className={`${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} shadow-sm overflow-hidden`}>
      <button
        onClick={onAdminToggle}
        className={`w-full flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['4']} hover:${COLOR_SCALE['surface-muted']} transition-colors`}
      >
        <div className={`flex items-center ${GAP_SCALE['3']}`}>
          <div
            className={`${SIZE_SCALE.w9} ${SIZE_SCALE['9']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['blue-50-bg']} flex items-center justify-center`}
          >
            <Shield className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${COLOR_SCALE.primary}`} />
          </div>
          <div className={`${TEXT_ALIGNMENT.left}`}>
            <p className={`${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground}`}>
              {t('navigation', 'admin_panel', 'Admin Panel')}
            </p>
            <p
              className={`${TYPE_SCALE.xs} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['admin-muted-text']}`}
            >
              {t('mobile_menu', 'admin_manage_site', 'Manage site')}
            </p>
          </div>
        </div>
        <ChevronDown
          className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} ${COLOR_SCALE['icon-muted']} transition-transform duration-200 ${
            adminOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {adminOpen && (
        <div
          className={`border-t ${COLOR_SCALE['admin-border']} ${COLOR_SCALE['surface-muted']} ${PADDING_X['2']} ${PADDING_Y['2']}`}
        >
          <Link
            to={createPageUrl('AdminDashboard')}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('admin', 'overview', 'Overview')}
          </Link>
          <Link
            to={createPageUrl('AdminDashboard') + '#clinical'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('admin', 'clinical', 'Clinical')}
          </Link>
          <Link
            to={createPageUrl('AdminDashboard') + '#inquiries'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('admin', 'inquiries', 'Inquiries')}
          </Link>
          <Link
            to={createPageUrl('AdminDashboard') + '#reports'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('navigation', 'report', 'Report')}
          </Link>
          <Link
            to={createPageUrl('AdminDashboard') + '#blog'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('admin', 'blog', 'Blog')}
          </Link>
          <Link
            to={createPageUrl('AdminDashboard') + '#usermanagement'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('admin', 'user_management', 'User Management')}
          </Link>
        </div>
      )}
    </div>
  );
};

// Helper function to render partner section
const renderPartnerSection = (
  user: UserSectionUser,
  partnerOpen: boolean,
  onPartnerToggle: () => void,
  onClose: () => void,
  newInquiryCount: number,
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string
) => {
  if (!user.partner_permissions) return null;

  return (
    <div className={`${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} shadow-sm overflow-hidden`}>
      <button
        onClick={onPartnerToggle}
        className={`w-full flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['4']} hover:${COLOR_SCALE['surface-muted']} transition-colors`}
      >
        <div className={`flex items-center ${GAP_SCALE['3']}`}>
          <div
            className={`${SIZE_SCALE.w9} ${SIZE_SCALE['9']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['purple-50-bg']} flex items-center justify-center`}
          >
            <Settings
              className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${COLOR_SCALE['purple-600']}`}
            />
          </div>
          <div className={`${TEXT_ALIGNMENT.left}`}>
            <p className={`text-sm ${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground}`}>
              {t('partnerdashboard', 'partner_dashboard', 'Partner Dashboard')}
            </p>
            {newInquiryCount > 0 ? (
              <p className={`text-xs ${COLOR_SCALE['red-600']} ${FONT_WEIGHTS.normal}`}>
                {t('mobile_menu', 'new_inquiries', '{{count}} new inquiries', {
                  count: newInquiryCount,
                })}
              </p>
            ) : (
              <p
                className={`${TYPE_SCALE.xs} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['admin-muted-text']}`}
              >
                {t('mobile_menu', 'partner_manage_content', 'Manage content')}
              </p>
            )}
          </div>
        </div>
        <ChevronDown
          className={`${(SIZE_SCALE as ExtendedSizeScale).iconMd} ${COLOR_SCALE['icon-muted']} transition-transform duration-200 ${
            partnerOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {partnerOpen && (
        <div
          className={`border-t ${COLOR_SCALE['admin-border']} ${COLOR_SCALE['surface-muted']} ${PADDING_X['2']} ${PADDING_Y['2']}`}
        >
          <Link
            to={createPageUrl('PartnerDashboard') + '#providers'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('navigation', 'providers', 'Providers')}
          </Link>
          <Link
            to={createPageUrl('PartnerDashboard') + '#treatments'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('navigation', 'treatments', 'Treatments')}
          </Link>
          <Link
            to={createPageUrl('PartnerDashboard') + '#doctors'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('navigation', 'doctors', 'Doctors')}
          </Link>
          <Link
            to={createPageUrl('PartnerDashboard') + '#inquiries'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('admin', 'inquiries', 'Inquiries')}
            {newInquiryCount > 0 && (
              <Badge
                variant="destructive"
                className={`${MARGIN_LEFT['2']} ${RADIUS_SCALE.full} ${(SIZE_SCALE as ExtendedSizeScale).minH24} flex items-center justify-center`}
              >
                {newInquiryCount}
              </Badge>
            )}
          </Link>
          <Link
            to={createPageUrl('PartnerDashboard') + '#analytics'}
            onClick={onClose}
            className={`block ${PADDING_X['3']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all border-b ${COLOR_SCALE['admin-border-subtle']} last:border-b-0`}
          >
            {t('partnerdashboard', 'analytics_tab', 'Analytics')}
          </Link>
        </div>
      )}
    </div>
  );
};

export const UserSection = ({
  user,
  newInquiryCount,
  adminOpen,
  partnerOpen,
  onAdminToggle,
  onPartnerToggle,
  handleLogin,
  handleSignup,
  handleLogout,
  onClose,
  t,
}: UserSectionProps) => {
  if (!user) {
    return (
      <div
        className={`${SURFACE_PADDING['4']} ${COLOR_SCALE.surface} border-t ${COLOR_SCALE['admin-border']} ${SPACE_Y_SCALE['2']}`}
      >
        <Button
          onClick={() => {
            handleLogin();
            onClose();
          }}
          className={`w-full font-medium ${COLOR_SCALE['interactive-primary-bg']} ${COLOR_SCALE['interactive-primary-bg-hover']} ${COLOR_SCALE['text-on-dark']} ${RADIUS_SCALE.xl} ${PADDING_Y['4']} shadow-sm hover:${SHADOW_SCALE.md} ${INTERACTIVE_STATES.transitions.all} ${INTERACTIVE_STATES.focus.ring}`}
        >
          {t('nav', 'log_in', 'Log In')}
        </Button>
        <Button
          onClick={() => {
            handleSignup();
            onClose();
          }}
          variant="outline"
          className={`w-full font-medium ${BORDER_WIDTH['2']} ${COLOR_SCALE['admin-border']} ${COLOR_SCALE['admin-heading']} hover:${COLOR_SCALE['surface-muted']} hover:${COLOR_SCALE['admin-border']} ${RADIUS_SCALE.xl} ${PADDING_X['2.5']} ${PADDING_Y['4']} ${INTERACTIVE_STATES.transitions.all} ${INTERACTIVE_STATES.focus.ring.replace('ring-primary/40', 'ring-gray-400')}`}
        >
          {t('nav', 'sign_up', 'Sign Up')}
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`${SURFACE_PADDING['4']} ${COLOR_SCALE.surface} border-t ${COLOR_SCALE['admin-border']} ${SPACE_Y_SCALE['2']}`}
    >
      {/* Admin Dashboard - Expandable */}
      {renderAdminSection(user, adminOpen, onAdminToggle, onClose, t)}

      {/* Partner Dashboard - Expandable */}
      {renderPartnerSection(user, partnerOpen, onPartnerToggle, onClose, newInquiryCount, t)}

      {/* User Profile Section - Combined for all logged-in users */}
      <div className={`${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} shadow-sm overflow-hidden`}>
        <div className={`flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['3']}`}>
          {/* Left: Profile Photo and Name (Clickable) */}
          <Link
            to={createPageUrl('UserDashboard')}
            onClick={onClose}
            className={`${FLEX.flex} ${FLEX_ALIGN.center} ${GAP_SCALE['3']} ${FLEX.flex1} ${SIZE_SCALE.minW0} hover:opacity-80 transition-opacity`}
          >
            <div
              className={`${SIZE_SCALE.w10} ${SIZE_SCALE['10']} ${RADIUS_SCALE.full} bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ${INTERACTIVE_STATES.rings.hover}`}
            >
              {user.profile_image_url ? (
                <img
                  src={user.profile_image_url}
                  alt={user.full_name ?? t('profile', 'profile_image_alt', 'Profile')}
                  className={`${SIZE_SCALE.full} h-full object-cover`}
                />
              ) : (
                <User className={`${(SIZE_SCALE as ExtendedSizeScale).iconMd} text-white`} />
              )}
            </div>
            <div className={`${FLEX.flex1} ${SIZE_SCALE.minW0}`}>
              <div className={`${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground} truncate`}>
                {user.full_name ?? t('nav', 'user', 'User')}
              </div>
              <div className={`${TYPE_SCALE.xs} ${COLOR_SCALE['admin-muted-text']} truncate`}>
                {user.email}
              </div>
            </div>
          </Link>

          {/* Right: Logout Button */}
          <button
            onClick={() => {
              handleLogout();
              onClose();
            }}
            className={`${MARGIN_LEFT['2']} ${SURFACE_PADDING['3']} ${RADIUS_SCALE.lg} hover:bg-red-50 transition-colors group flex-shrink-0`}
            aria-label={t('nav', 'logout', 'Log Out')}
          >
            <LogOut
              className={`${(SIZE_SCALE as ExtendedSizeScale).iconMd} ${COLOR_SCALE['icon-muted']} group-hover:${COLOR_SCALE['red-600-text']} transition-colors`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};
